/**
 * Product Info - Variant Switching Handler
 * 
 * Handles updating product variant information when variants change
 * Updates: SKU, Barcode, Weight, and Warehouse/Bin Location
 * 
 * This replaces the functionality that was previously in global.js
 * and is specifically designed for Dawn 15.4.0's variant switching system
 */

// Check if ProductVariantInfo already exists to prevent duplicate declarations
if (typeof window.ProductVariantInfo === 'undefined') {
  class ProductVariantInfo {
    constructor() {
      this.variantData = null;
      this.currentVariantId = null;
      this.init();
    }

    init() {
      // Load variant data from the JSON script tag
      this.loadVariantData();
      
      // Get initial variant ID
      this.getCurrentVariantId();
      
      // Set up event listeners for variant changes
      this.setupEventListeners();
      
      // Update info for current variant
      this.updateVariantInfo();
    }

    loadVariantData() {
      const variantDataElement = document.getElementById('product-variant-data');
      if (variantDataElement) {
        try {
          const data = JSON.parse(variantDataElement.textContent);
          this.variantData = data.variants;
        } catch (error) {
          console.error('Error parsing variant data:', error);
        }
      }
    }

    getCurrentVariantId() {
      // Try to get from hidden input
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        this.currentVariantId = parseInt(variantInput.value);
        return;
      }

      // Try to get from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const variantParam = urlParams.get('variant');
      if (variantParam) {
        this.currentVariantId = parseInt(variantParam);
        return;
      }

      // Fallback to first variant if available
      if (this.variantData && this.variantData.length > 0) {
        this.currentVariantId = this.variantData[0].id;
      }
    }

    setupEventListeners() {
      // Listen for variant changes from the variant picker
      document.addEventListener('variant:change', (event) => {
        if (event.detail && event.detail.variant) {
          this.currentVariantId = event.detail.variant.id;
          this.updateVariantInfo();
        }
      });

      // Also listen for changes to the variant input
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
              const newVariantId = parseInt(variantInput.value);
              if (newVariantId && newVariantId !== this.currentVariantId) {
                this.currentVariantId = newVariantId;
                this.updateVariantInfo();
              }
            }
          });
        });
        
        observer.observe(variantInput, { attributes: true });
        
        // Also listen for input changes
        variantInput.addEventListener('change', () => {
          const newVariantId = parseInt(variantInput.value);
          if (newVariantId && newVariantId !== this.currentVariantId) {
            this.currentVariantId = newVariantId;
            this.updateVariantInfo();
          }
        });
      }
    }

    updateVariantInfo() {
      if (!this.variantData || !this.currentVariantId) {
        return;
      }

      // Find the current variant
      const currentVariant = this.variantData.find(variant => variant.id === this.currentVariantId);
      if (!currentVariant) {
        return;
      }

      // Update SKU using class selector
      this.updateElementByClass('product_sku', currentVariant.sku || 'N/A');
      
      // Update Barcode using class selector  
      this.updateElementByClass('bar_code', currentVariant.barcode || 'N/A');
      
      // Update Weight using class selector
      if (currentVariant.weight && currentVariant.weight > 0) {
        this.updateElementByClass('package_weight', `${currentVariant.weight}g`);
      } else {
        this.updateElementByClass('package_weight', 'N/A');
      }
      
      // Update Warehouse/Bin Location using class selector
      this.updateElementByClass('bin_location', currentVariant.bin_location || 'N/A');
    }

    updateElement(elementId, content) {
      const element = document.getElementById(elementId);
      if (element) {
        // Handle both text content and data attributes
        if (element.hasAttribute('data-variant-info')) {
          element.setAttribute('data-variant-info', content);
        }
        element.textContent = content;
      }
    }

    updateElementByClass(className, content) {
      const element = document.querySelector(`.${className}`);
      if (element) {
        element.textContent = content;
      }
    }
  }

  // Make the class available globally and initialize it
  window.ProductVariantInfo = ProductVariantInfo;

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new ProductVariantInfo();
    });
  } else {
    new ProductVariantInfo();
  }
} else {
  // Avoid duplicate initialization
  // ProductVariantInfo already exists, skipping initialization
}
